import { debounce } from '@orangelv/utils';
import { useEffect, useRef } from 'react';

import clearCanvas from './clear-canvas.js';
import digest from './digest.js';
import { monitorMemoryUsage } from './monitor-memory-usage.js';
import resizeScene from './resize-scene.js';
import { getState, setDigesting, setProps } from './state.js';
import type { Props, Ref, State } from './types.js';
import usePrevious from './use-previous.js';

const DIGEST_DEBOUNCE = 100;

const useRenderer = (stateRef: Ref<State>, props: Props): void => {
  const previousProps = usePrevious(props);
  const pendingDigestData = useRef<
    { props: Props; previousProps?: Props | undefined } | undefined
  >(undefined);

  const digestDebouncedRef = useRef(
    debounce(async (digestProps: Props, previousDigestProps?: Props) => {
      const { canvasRef, coverCanvasRef, isDigesting, isGeneratingPreviews } =
        getState(stateRef);

      if (!canvasRef.current || !coverCanvasRef.current) {
        return;
      }

      if (isGeneratingPreviews) {
        return;
      }

      if (isDigesting) {
        // If there's a digest already happening, don't digest the new props just yet.
        // Instead update the props that are scheduled for digest.
        pendingDigestData.current = {
          props: digestProps,
          previousProps: previousDigestProps,
        };
        return;
      }

      setDigesting(stateRef)(true);
      setProps(stateRef)(digestProps, previousDigestProps);
      await digest(stateRef);
      setDigesting(stateRef)(false);

      if (pendingDigestData.current) {
        // Digest the scheduled props, and reset them so we're ready for the next changes.
        const propsToDigest = pendingDigestData.current;
        pendingDigestData.current = undefined;
        digestDebouncedRef.current(
          propsToDigest.props,
          propsToDigest.previousProps,
        );
      }
    }, DIGEST_DEBOUNCE),
  );

  useEffect(() => {
    digestDebouncedRef.current(props, previousProps);
  }, [props, previousProps]);

  const { isHidden } = props;
  const resizeSceneRef = useRef(() => {
    resizeScene(stateRef, isHidden)();
  });

  useEffect(() => {
    const listener = resizeSceneRef.current;
    if ('window' in globalThis) {
      globalThis.window.addEventListener('resize', listener);
    }

    return (): void => {
      if ('window' in globalThis) {
        globalThis.window.removeEventListener('resize', listener);
      }
    };
  }, []);

  useEffect(() => {
    resizeScene(stateRef, isHidden)();
  }, [props.autoResize, isHidden, stateRef]);

  useEffect(() => {
    if (isHidden) {
      clearCanvas(stateRef)();
    }
  }, [isHidden, stateRef]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      cleanup = monitorMemoryUsage();
      // Start after 10s, to allow the initial model load to complete.
      // In this case we don't care about the initial load, just that the memory isn't leaking while using the customizer.
    }, 10_000);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
    };
  }, []);

  useEffect(
    () => () => {
      const state = getState(stateRef);

      if (state.scene) {
        const engine = state.scene.getEngine();
        state.scene.dispose();
        engine.dispose();
      }
    },
    [stateRef],
  );
};

export default useRenderer;
